/**
 * Configuration Manager Component
 * Handles saving, loading, and managing focus group configurations
 */
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save, FolderOpen, Download, Upload, Trash2, X, Check, Clock, FileText
} from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

export function ConfigManager({
  savedConfigs,
  onSave,
  onLoad,
  onDelete,
  onExport,
  onImport,
  currentConfigName = '',
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const configList = Object.values(savedConfigs).sort(
    (a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
  );

  const handleSave = () => {
    if (!saveName.trim()) return;
    onSave(saveName.trim());
    setSaveName('');
    setShowSaveDialog(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const result = await onImport(file);
      if (result.imported) {
        alert(t('config.imported', { count: result.imported }));
      } else if (result.error) {
        alert(t('config.importError', { error: result.error }));
      }
    }
    e.target.value = '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowSaveDialog(true)}
          className="btn btn-secondary flex items-center gap-2"
          title={t('config.saveTitle')}
        >
          <Save className="w-4 h-4" />
          {t('config.save')}
        </button>

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="btn btn-secondary flex items-center gap-2"
          title={t('config.loadTitle')}
        >
          <FolderOpen className="w-4 h-4" />
          {t('config.load')}
          {configList.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
              {configList.length}
            </span>
          )}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          className="hidden"
        />

        <button
          onClick={() => fileInputRef.current?.click()}
          className="btn btn-secondary flex items-center gap-2"
          title={t('config.importTitle')}
        >
          <Upload className="w-4 h-4" />
        </button>
      </div>

      {/* Success Message */}
      <AnimatePresence>
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute top-full left-0 mt-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            {t('config.saved')}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Dialog */}
      <AnimatePresence>
        {showSaveDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowSaveDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-serif text-xl mb-4">{t('config.saveDialog')}</h3>

              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder={t('config.namePlaceholder')}
                className="input w-full mb-4"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="btn btn-secondary"
                >
                  {t('config.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!saveName.trim()}
                  className="btn btn-primary"
                >
                  {t('config.save')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Load Dialog */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-xl">{t('config.savedConfigs')}</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-stone-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {configList.length === 0 ? (
                <div className="text-center py-12 text-stone-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t('config.noSaved')}</p>
                  <p className="text-sm mt-1">
                    {t('config.noSavedDesc')}
                  </p>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-2">
                  {configList.map((cfg) => (
                    <div
                      key={cfg.id}
                      className="border border-stone-200 rounded-lg p-4 hover:border-amber-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-stone-900 truncate">
                            {cfg.name}
                          </h4>
                          {cfg.config?.topic && (
                            <p className="text-sm text-stone-500 truncate mt-1">
                              {cfg.config.topic}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(cfg.updatedAt || cfg.createdAt)}
                            </span>
                            {cfg.config && (
                              <span>
                                {cfg.config.participantCount || 5} {t('config.participants')},{' '}
                                {cfg.config.rounds || 5} {t('config.rounds')}
                              </span>
                            )}
                            {cfg.personas?.length > 0 && (
                              <span className="text-amber-600">
                                {t('config.personasSaved')}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 ml-4">
                          <button
                            onClick={() => {
                              onLoad(cfg.id);
                              setIsOpen(false);
                            }}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                            title={t('config.loadBtn')}
                          >
                            <FolderOpen className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onExport(cfg.id)}
                            className="p-2 text-stone-500 hover:bg-stone-100 rounded-lg"
                            title={t('config.exportJSON')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(t('config.deleteConfirm'))) {
                                onDelete(cfg.id);
                              }
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            title={t('config.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-stone-200 flex justify-between">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {t('config.importFromFile')}
                </button>

                {configList.length > 0 && (
                  <button
                    onClick={() => {
                      const blob = new Blob(
                        [JSON.stringify(savedConfigs, null, 2)],
                        { type: 'application/json' }
                      );
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${t('export.allConfigsFilename')}_${Date.now()}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    {t('config.exportAll')}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ConfigManager;
